import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { ChildrenStoreData } from 'src/shared/types/src';
import { useAppSelector } from '../../services/store';
import { getInvoices } from 'src/shared/services/src';
import { getFormattedAmount, formatUnixTimestamp } from 'src/shared/utils/src';
import LocalImages from "../../static";
import InvoiceDetailsModal from './InvoiceDetailsModal';
import DepositPaymentDetailsModal from './DepositPaymentDetailsModal';
import PaymentModal from './PaymentModal';
import { getCurrentChild } from 'src/shared/redux-state/src';
import { InvoiceResponseType, TransactionType } from 'src/shared/types/src/lib/invoice.types'
import { APP_MESSAGES } from 'src/shared/config/src';
import { CustomButton, InvoiceLoader } from 'src/app/components';
import { BaseCard } from 'src/app/components';
import { toast } from "react-toastify";
import ProfileLayout from 'src/app/layouts/ProfileLayout';
import Images from '../../static';
import { ShimmerThumbnail } from "react-shimmer-effects";
import { navigateToDownloadAppScreen } from 'src/app/utilities/helpers';
import { GoArrowSwitch } from "react-icons/go";

const InvoiceScreen = () => {

    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

    const [invoiceData, setInvoiceData] = useState<InvoiceResponseType>();
    const [loading, setLoading] = useState<boolean>(false);
    const [currentItem, setCurrentItem] = useState<TransactionType>();
    const [isBillPayer, setBillPayer] = useState<boolean>(false);
    const [isInvoiceDetailsModal, setIsInvoiceDetailsModal] = useState<boolean>(false);
    const [isDepositPaymentDetailsModal, setIsDepositPaymentDetailsModal] = useState<boolean>(false);
    const [isPaymentModal, setIsPaymentModal] = useState<boolean>(false);

    const TYPE_PAYMENT = 'Payment';
    const TYPE_INVOICE = 'Invoice';
    const TYPE_DEPOSIT = 'Deposit'
    const TYPE_REFUND = 'Refund/Transfer';
    const TYPE_CREDIT_NOTE = 'Credit Note';

    useEffect(() => {
        init()
    }, []);

    const init = async () => {
        try {
            setLoading(true);
            const response = await getInvoices({ child: currentChild });
            if (response && Object.keys(response).length > 0) {
                setInvoiceData(response);
                setBillPayer(true);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                const apiError = error as AxiosError;
                if (apiError && apiError.response && apiError.response.status === 403) {
                    setBillPayer(false);
                } else {
                    const { errors } = error?.response?.data;
                    if (errors && Object.keys(errors).length > 0) {
                        toast.error(errors.length > 0 ? errors.join(',') : errors.join(", "), { autoClose: 5000 });
                    } else {
                        toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
                    };
                }
            } else {
                toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
            }
        } finally {
            setLoading(false)
        }
    };

    const getCardIcon = (type: string) => {
        switch (type) {
            case TYPE_PAYMENT:
                return <img src={Images.payment} alt="payment-icon" />
            case TYPE_INVOICE:
                return <img src={Images.invoiceImg} alt="invoice-icon" />
            case TYPE_REFUND:
                return <GoArrowSwitch />
            case TYPE_CREDIT_NOTE:
                return <img src={Images.invoicePaymentIcon} alt="invoice-payment-icon" />
            default:
                return <img src={Images.deposit} alt="deposit-icon" />
        }
    };

    const toggleInvoiceDetailsModal = () => {
        setIsInvoiceDetailsModal(!isInvoiceDetailsModal);
    }

    const toggleDepositPaymentDetailsModal = () => {
        setIsDepositPaymentDetailsModal(!isDepositPaymentDetailsModal);
    }

    const togglePaymentModal = () => {
        setIsPaymentModal(!isPaymentModal);
    }

    const renderCards = (item: TransactionType) => {
        let title = item.type;

        switch (item?.type) {
            case TYPE_PAYMENT:
                title = "Payment";
                break;
            case TYPE_INVOICE:
                title = "Invoice";
                break;
            case TYPE_DEPOSIT:
                title = "Deposit";
                break;
            default:
                title = item?.type;
        };

        return (
            <BaseCard
                clickable={true}
                onPressFunc={() => {
                    setCurrentItem(item)
                    if (item?.type === TYPE_INVOICE) {
                        toggleInvoiceDetailsModal()
                    } else {
                        toggleDepositPaymentDetailsModal()
                    }
                }}
                customBaseCardStyle='pd-15 mg-b-15'
            >
                <div className='pd-l-45 position-relative d-flex align-items-center'>
                    <div className='invoice-payment-icon border border-gray-200 position-absolute start-0 d-inline-flex align-items-center justify-content-center rounded-circle'>
                        {getCardIcon(item?.type)}
                    </div>
                    <div className='d-flex flex-column flex-1'>
                        <div className='fw-semibold fs-14 text-black'>{title} 
                            {item?.type === TYPE_INVOICE ? `(${item?.details?.txid})` : ''}
                        </div>
                        <div className='fs-12 text-gray-300'>{formatUnixTimestamp(item?.timestamp)}</div>
                    </div>
                    <div className='text-end'>
                        <div className='fs-12 fw-bold text-black'>{item?.amount && invoiceData?.currency ? getFormattedAmount(item?.amount, invoiceData?.currency) : '-'}</div>
                        <div className='fs-12 text-black'>Balance: {item?.balance && invoiceData?.currency ? getFormattedAmount(item?.balance, invoiceData?.currency) : '£0.00'}</div>
                    </div>
                </div>
            </BaseCard>
        )
    };

    const renderBalanceLoader = () => {
        return (
            <div style={{ width: '40px' }}>
               <ShimmerThumbnail height={13} className="rounded-3 mb-0" />
            </div>
        )
    }

    const callbackOnSuccess = () => {
        init();
    }

    return (
        <ProfileLayout title="Invoice and Payments">

            <div className='d-flex align-items-center gap-3 mg-t-10'>
                <div className='w-50'>
                    <div className='d-flex align-items-center border border-gray-200 pd-x-15 pd-y-8 rounded-2'>
                        <div className='flex-1 pd-r-10'>
                            <p className='fw-bold fs-12 text-black'>Balance</p>
                            {loading && renderBalanceLoader()}
                            {!loading && <p className='fs-12 text-black mg-b-0'>{(invoiceData?.balance && isBillPayer) ? getFormattedAmount(invoiceData?.balance, invoiceData?.currency) : '£0.00'}</p>}
                        </div>
                        <div><img src={Images.balance} alt="Balance Icon" /></div>
                    </div>
                </div>

                <div className='w-50'>
                    <div className='d-flex align-items-center border border-gray-200 pd-x-15 pd-y-8 rounded-2'>
                        <div className='flex-1 pd-r-10'>
                            <p className='fw-bold fs-12 text-black'>Deposit</p>
                            {loading && renderBalanceLoader()}
                            {!loading && <p className='fs-12 text-black mg-b-0'>{(invoiceData?.depositAmount && isBillPayer) ? getFormattedAmount(invoiceData?.depositAmount, invoiceData?.currency) : '£0.00'}</p>}
                        </div>
                        <div><img src={Images.deposit} alt="Balance Icon" /></div>
                    </div>
                </div>
            </div>

            {loading && <InvoiceLoader />}

            {(!loading && invoiceData && Object.keys(invoiceData).length === 0) ?
                <div className='d-flex align-items-center justify-content-center flex-column flex-grow-1'>
                    <img src={LocalImages.invoice} alt="notABillPayer" style={{ width: "100px", height: "100px" }} />
                    <p className='text-center mb-0 fs-6 fw-semibold mg-t-20'>No data found.</p>
                </div> : null
            }

            {(!loading && isBillPayer) ?
                <div className='mg-t-20 flex-1'>
                    {invoiceData?.transactions.map(item => {
                        return renderCards(item);
                    })}
                </div> : null
            }

            {
                (!loading && !isBillPayer) ?
                    <>
                        <div className='d-flex align-items-center justify-content-center flex-column flex-grow-1'>
                            <img src={LocalImages.notABillPayer} alt="notABillPayer" style={{ width: "100px", height: "100px" }} />
                            <p className='text-center mb-0 fs-6 fw-semibold mg-t-20'>You have not been added as a bill payer</p>
                        </div>
                    </> : null
            }


            {
                (isBillPayer && invoiceData?.paymentGateway?.connected) ?
                    <CustomButton
                        onClick={navigateToDownloadAppScreen}
                        text='Pay Now'
                        color="primary"
                        disabled={loading}
                    /> : null
            }

            <InvoiceDetailsModal
                invoiceData={currentItem?.details}
                currency={invoiceData?.currency}
                isInvoiceDetailsModal={isInvoiceDetailsModal}
                toggleInvoiceDetailsModal={toggleInvoiceDetailsModal}
            />

            <DepositPaymentDetailsModal
                paymentData={currentItem?.details}
                currency={invoiceData?.currency}
                isDepositPaymentDetailsModal={isDepositPaymentDetailsModal}
                toggleDepositPaymentDetailsModal={toggleDepositPaymentDetailsModal}
            />

            <PaymentModal
                currency={invoiceData?.currency}
                balance={invoiceData?.balance}
                type={invoiceData?.paymentGateway?.type || ''}
                callbackOnSuccess={callbackOnSuccess}
                isPaymentModal={isPaymentModal}
                togglePaymentModal={togglePaymentModal}
            />
        </ProfileLayout>
    )
};

export default InvoiceScreen;